export type WalletErrorKind = 'account_missing' | 'rejected' | 'unavailable';

/**
 * Read by shape: `ApiError` lives in `lib/util`, which builds the API client on import
 * and so stays out of the Node unit tests.
 */
const statusOf = (error: unknown) => {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' ? status : undefined;
};

const isClientError = (error: unknown) => {
  const status = statusOf(error);
  return status !== undefined && status >= 400 && status < 500;
};

/** The wallet endpoints answer 404 until Swan has opened the account. */
export const walletErrorKind = (error: unknown): WalletErrorKind => {
  if (statusOf(error) === 404) return 'account_missing';
  return isClientError(error) ? 'rejected' : 'unavailable';
};

const UNREACHABLE_STATUSES = [502, 503, 504];

/**
 * A network error carries no status; 502, 503 and 504 mean the gateway could not reach the
 * service. A 500 is the service answering with a fault, which the page must show, not hide
 * behind an older summary.
 */
export const isServiceUnreachable = (error: unknown) => {
  const status = statusOf(error);
  return status === undefined || UNREACHABLE_STATUSES.includes(status);
};

const MAX_RETRIES = 3;

/** A 4xx will not change on a second try; only a service that fails to answer is retried. */
export const shouldRetryCardQuery = (failureCount: number, error: unknown) =>
  !isClientError(error) && failureCount < MAX_RETRIES;
